import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability } from "@/auth/capabilities";
import { AddControllerDialog } from "@/components/add-controller-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AdminFilterGroup, AdminFilterPanel, AdminListLayout } from "@/components/admin-filter-panel";
import { ListFilter } from "@/components/list-filter";
import { ListPagination, ListSearch, ListSort, useBrowsingState, usePagedList } from "@/components/list-controls";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { formatControllerName, formatDeviceId, formatRelative } from "@/lib/format";

export function ControllersPage(): JSX.Element {
  const [addOpen, setAddOpen] = useState(false);
  const [vendor, setVendor] = useBrowsingState("controllers:vendor", "");
  const [site, setSite] = useBrowsingState("controllers:site", "");
  const [connectionStatus, setConnectionStatus] = useBrowsingState("controllers:connection-status", "");
  const [provisioningStatus, setProvisioningStatus] = useBrowsingState("controllers:provisioning-status", "");
  const { me } = useAuth();
  const canAdd = hasCapability(me, "provisioning:write");
  const controllerOptions = useQuery({
    queryKey: ["controllers", "browse-options"],
    queryFn: () => api.controllerBrowseOptions(),
  });

  const { list, query: controllersQuery } = usePagedList("controllers", {
    vendor_id: vendor, site_id: site, connection_status: connectionStatus, provisioning_status: provisioningStatus,
  }, [
    { key: "name", label: "Name" }, { key: "device_id", label: "Device ID" },
    { key: "site", label: "Site" }, { key: "vendor", label: "Vendor" },
    { key: "connection_status", label: "Connection status" }, { key: "provisioning_status", label: "Provisioning status" },
  ], api.browseControllers);
  const vendorOptions = (controllerOptions.data?.vendors ?? []).map((vendorOption) => ({
    value: String(vendorOption.tenant_id), label: vendorOption.name,
  }));
  const siteOptions = (controllerOptions.data?.sites ?? []).map((value) => ({ value, label: value }));
  const connectionOptions = (controllerOptions.data?.connection_statuses ?? []).map((value) => ({ value, label: value }));
  const provisioningOptions = (controllerOptions.data?.provisioning_statuses ?? []).map((value) => ({ value, label: value }));

  return (
    <>
      <PageHeader
        title="Controllers"
        description="Controller-first configuration view. Meters and feeder assignments live here."
        actions={
          <div className="flex w-full min-w-0 flex-wrap gap-2 sm:w-auto">
            <Button variant="outline" onClick={() => void controllersQuery.refetch()}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
            {canAdd ? (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add Controller
              </Button>
            ) : null}
          </div>
        }
      />

      {controllersQuery.error ? (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {controllersQuery.error instanceof Error
            ? controllersQuery.error.message
            : "Failed to load controllers"}
        </div>
      ) : null}
      {controllerOptions.error ? <div role="alert" className="mb-4 text-sm text-destructive">
        Could not load controller filter choices. <Button variant="outline" size="sm" onClick={() => void controllerOptions.refetch()}>Retry</Button>
      </div> : null}

      <AdminListLayout filters={
        <AdminFilterPanel count={[vendor, site, connectionStatus, provisioningStatus, list.search.trim()].filter(Boolean).length} onClear={() => {
          setVendor(""); setSite(""); setConnectionStatus(""); setProvisioningStatus(""); list.setSearch("");
        }}>
          <AdminFilterGroup label="Controller">
            <ListFilter label="Vendor filter" value={vendor} options={vendorOptions} onChange={(value) => {
              setVendor(value); setSite("");
            }} />
            <ListFilter label="Site filter" value={site} options={siteOptions} onChange={setSite} />
            <ListFilter label="Connection status filter" value={connectionStatus} options={connectionOptions} onChange={setConnectionStatus} />
            <ListFilter label="Provisioning status filter" value={provisioningStatus} options={provisioningOptions} onChange={setProvisioningStatus} />
          </AdminFilterGroup>
          <AdminFilterGroup label="Search">
            <ListSearch label="Search controllers" placeholder="Search controllers, site, tenant…" value={list.search} onChange={list.setSearch} />
            <ListSort {...list} />
          </AdminFilterGroup>
        </AdminFilterPanel>
      }>
      <Card className="min-w-0 max-w-full">
        <CardContent className="min-w-0 overflow-x-auto p-0">
          {controllersQuery.isLoading ? (
            <div className="p-6">
              <Skeleton className="h-96 w-full" />
            </div>
          ) : controllersQuery.error && !controllersQuery.data ? (
            <div className="p-6 text-sm">
              Unable to load controllers.{" "}
              <Button variant="outline" size="sm" onClick={() => void controllersQuery.refetch()}>Retry</Button>
            </div>
          ) : (
            <table className="w-full min-w-[900px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Controller</th>
                  <th className="px-4 py-3">Tenant</th>
                  <th className="px-4 py-3">Site</th>
                  <th className="px-4 py-3">Meters</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {list.total === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-muted-foreground" colSpan={6}>
                      {[vendor, site, connectionStatus, provisioningStatus, list.search.trim()].some(Boolean) ? "No controllers match the current filter." : "No controllers found."}
                    </td>
                  </tr>
                ) : (
                  list.rows.map((controller) => (
                    <tr key={controller.device_id} className="border-b border-border last:border-0 hover:bg-accent/40">
                      <td className="px-4 py-3">
                        <Link
                          to={`/controllers/${encodeURIComponent(controller.device_id)}`}
                          className="font-semibold text-foreground hover:text-primary"
                        >
                          <div>{formatControllerName(controller.display_name, controller.serial_alias)}</div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {formatDeviceId(controller.device_id)}
                          </div>
                        </Link>
                      </td>
                      <td className="px-4 py-3">{controller.tenant_name ?? "Unassigned"}</td>
                      <td className="px-4 py-3">{controller.site_id}</td>
                      <td className="px-4 py-3 tabular-nums">{controller.meter_count ?? "Unknown"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {controller.provisioning_status} / {controller.connection_status}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {formatRelative(controller.latest_telemetry_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </CardContent>
        <ListPagination {...list} />
      </Card>
      </AdminListLayout>

      {canAdd ? <AddControllerDialog open={addOpen} onOpenChange={setAddOpen} /> : null}
    </>
  );
}
